import { PageHero } from "@/components/site/Blocks";
import { supabase } from "@/integrations/supabase/client";
import type { Crumb } from "@/lib/schema";

export type TermsTable = { header: string[]; rows: string[][] };
export type TermsClause = {
  num: string | null;
  text: string;
  table?: TermsTable;
  bullets?: string[];
  after?: string;
};
export type TermsSection = { heading: string | null; clauses: TermsClause[] };
export type TermsPart = { title: string | null; intro: string | null; sections: TermsSection[] };
export type TermsContent = {
  lang: string;
  title: string;
  company: string;
  subtitle: string;
  version: string;
  stand: string;
  notice: string;
  labels: { version: string; stand: string; toc: string; download: string };
  imprint: string;
  parts: TermsPart[];
};

/** Loads a legal document verbatim from the database (never rewritten in code). */
export async function loadLegalDocument(docKey: "agb" | "eula" | "sla", lang: "en" | "tr"): Promise<TermsContent> {
  const { data, error } = await supabase
    .from("legal_documents")
    .select("content")
    .eq("doc_key", docKey)
    .eq("lang", lang)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Legal document not found");
  return data.content as unknown as TermsContent;
}

export const loadTerms = (lang: "en" | "tr") => loadLegalDocument("agb", lang);

const slug = (num: string) => num.toLowerCase().replace(/\./g, "-");
/** "5.8" -> "section-5", "B.4.7" -> "section-b-4". */
const sectionId = (s: TermsSection, i: number) => {
  const num = s.clauses[0]?.num;
  if (!num) {
    const headingNum = s.heading?.match(/^([A-Za-z]?\.?\d+)/)?.[1];
    return headingNum ? `section-${slug(headingNum)}` : `section-x-${i + 1}`;
  }
  return `section-${slug(num.split(".").slice(0, -1).join("."))}`;
};
const partId = (i: number) => `part-${i + 1}`;

function LegalTable({ table }: { table: TermsTable }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-border" data-legal-table>
      <table className="w-full min-w-max border-collapse text-left text-sm">
        <thead className="bg-secondary text-foreground">
          <tr>
            {table.header.map((cell, index) => (
              <th key={index} scope="col" className="border-b border-border px-4 py-3 font-bold">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-border last:border-b-0">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-4 py-3 align-top whitespace-pre-line">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const GERMAN_TERMS_URL = "https://www.oxory.de/agb";

export function TermsDocument({
  content,
  trail,
  eyebrow,
  originalUrl = GERMAN_TERMS_URL,
}: {
  content: TermsContent;
  trail: readonly Crumb[];
  eyebrow: string;
  originalUrl?: string;
}) {
  const { labels } = content;
  return (
    <>
      <div className="print:hidden">
        <PageHero trail={trail} eyebrow={eyebrow} title={content.title} intro={content.subtitle} />
      </div>
      <div className="section-shell py-12 print:py-0">
        <div className="mx-auto max-w-3xl">
          <h1 className="hidden print:block font-display text-2xl font-extrabold">{content.title}</h1>
          <aside
            role="note"
            className="rounded-xl border-l-4 border-primary bg-secondary p-5 text-sm text-foreground print:border print:bg-transparent"
          >
            <p>{content.notice}</p>
            <p className="mt-2">
              <a
                href={originalUrl}
                hrefLang="de"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline underline-offset-4"
              >
                {originalUrl}
              </a>
            </p>
          </aside>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <p>
              {labels.version} {content.version} · {labels.stand} {content.stand}
            </p>
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-border px-3 py-1.5 font-semibold text-foreground hover:bg-secondary print:hidden"
            >
              {labels.download}
            </button>
          </div>

          <nav aria-labelledby="terms-toc" className="mt-10 rounded-xl border border-border p-6 print:hidden">
            <h2 id="terms-toc" className="font-display text-lg font-extrabold text-foreground">
              {labels.toc}
            </h2>
            <ol className="mt-4 space-y-4 text-sm">
              {content.parts.map((part, pi) => (
                <li key={pi}>
                  {part.title && (
                    <a href={`#${partId(pi)}`} className="font-semibold text-foreground hover:text-primary">
                      {part.title}
                    </a>
                  )}
                  <ol className="mt-2 space-y-1 pl-4">
                    {part.sections.map((s, si) =>
                      s.heading ? (
                        <li key={si}>
                          <a href={`#${sectionId(s, si)}`} className="text-muted-foreground hover:text-primary">
                            {s.heading}
                          </a>
                        </li>
                      ) : null,
                    )}
                  </ol>
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-12 space-y-14">
            {content.parts.map((part, pi) => (
              <section key={pi} id={partId(pi)} className="scroll-mt-28">
                {part.title && <h2 className="font-display text-2xl font-extrabold text-foreground">{part.title}</h2>}
                {part.intro && <p className="mt-3 italic text-muted-foreground">{part.intro}</p>}
                <div className={part.title || part.intro ? "mt-8 space-y-10" : "space-y-10"}>
                  {part.sections.map((s, si) => (
                    <section key={si} id={sectionId(s, si)} className="scroll-mt-28 break-inside-avoid-page">
                      {s.heading && <h3 className="font-display text-lg font-bold text-foreground">{s.heading}</h3>}
                      <div className={s.heading ? "mt-4 space-y-3" : "space-y-3"}>
                        {s.clauses.map((c, ci) => (
                          <div
                            key={c.num ?? ci}
                            id={c.num ? `clause-${slug(c.num)}` : `clause-x-${si + 1}-${ci + 1}`}
                            className={
                              c.num
                                ? "grid scroll-mt-28 grid-cols-[3.75rem_minmax(0,1fr)] gap-3 text-muted-foreground print:text-foreground"
                                : "scroll-mt-28 text-muted-foreground print:text-foreground"
                            }
                          >
                            {c.num && <span className="font-bold text-foreground">{c.num}</span>}
                            <div className="min-w-0">
                              <p className="whitespace-pre-line">{c.text}</p>
                              {c.table && <LegalTable table={c.table} />}
                              {c.bullets && c.bullets.length > 0 && (
                                <ul className="mt-2 list-disc space-y-1 pl-5">
                                  {c.bullets.map((b, bi) => (
                                    <li key={bi}>{b}</li>
                                  ))}
                                </ul>
                              )}
                              {c.after && (
                                <div className="mt-2 space-y-2 pl-5">
                                  {c.after.split(/\n\n+/).map((paragraph, paragraphIndex) => (
                                    <p key={paragraphIndex} className="whitespace-pre-line">
                                      {paragraph}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <footer className="mt-16 border-t border-border pt-6 text-xs text-muted-foreground whitespace-pre-line">
            {content.imprint}
          </footer>
        </div>
      </div>
    </>
  );
}

export function TermsPending() {
  return (
    <div className="section-shell py-32 text-center text-muted-foreground" role="status">
      …
    </div>
  );
}

export function TermsError({ message, originalUrl = GERMAN_TERMS_URL }: { message: string; originalUrl?: string }) {
  return (
    <div className="section-shell py-32 text-center" role="alert">
      <p className="text-foreground">{message}</p>
      <p className="mt-3">
        <a href={originalUrl} className="text-primary underline underline-offset-4">
          {originalUrl}
        </a>
      </p>
    </div>
  );
}
